<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <span class="headline font-weight-bold">Gimnasio - Gestión de Rutinas y Coaches</span>
                    <v-spacer></v-spacer>
                    <v-btn color="secondary" text @click="goToCoachRoutines" class="mr-2" :disabled="!allowed">
                        <v-icon left>mdi-pencil</v-icon>
                        Crear Rutinas
                    </v-btn>
                    <v-btn color="primary" text @click="openAssignRoutineDialog" class="mr-2" :disabled="!allowed">
                        <v-icon left>mdi-plus</v-icon>
                        Asignar Rutina
                    </v-btn>
                </v-card-title>

                <v-alert v-if="errorMessage" type="error" dense outlined dismissible @input="errorMessage = ''">
                    {{ errorMessage }}
                </v-alert>
                <v-alert v-if="forbidden" type="warning" outlined>
                    No tienes permisos para administrar el gimnasio. Solicita el permiso de administrador de gimnasio o de coach.
                </v-alert>

                <!-- Tabs -->
                <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
                    <v-tab>
                        <v-icon left>mdi-dumbbell</v-icon>
                        Asignación de Rutinas
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-account-group</v-icon>
                        {{ isAdmin ? 'Socios' : 'Mis socios' }}
                    </v-tab>
                    <v-tab v-if="isAdmin">
                        <v-icon left>mdi-account-tie</v-icon>
                        Coaches de Gimnasio
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-history</v-icon>
                        Historial
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="activeTab" class="transparent">
                    <!-- Tab 1: Asignación de Rutinas -->
                    <v-tab-item>
                        <v-row class="mb-4">
                            <v-col cols="12" sm="6" :md="isAdmin ? 3 : 4">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold primary--text">{{ summary.total }}</div>
                                    <div class="caption grey--text">Rutinas Asignadas</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" :md="isAdmin ? 3 : 4">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold green--text">{{ summary.active }}</div>
                                    <div class="caption grey--text">Activas</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" :md="isAdmin ? 3 : 4">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold orange--text">{{ summary.completed }}</div>
                                    <div class="caption grey--text">Completadas</div>
                                </v-card>
                            </v-col>
                            <v-col v-if="isAdmin" cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold info--text">{{ summary.coaches }}</div>
                                    <div class="caption grey--text">Coaches</div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-card class="elevation-1 mb-4">
                            <v-card-subtitle class="pa-4 pb-0">
                                <v-icon small class="mr-2">mdi-dumbbell</v-icon>
                                Rutinas Asignadas a Socios
                            </v-card-subtitle>
                            <v-row class="px-4" dense>
                                <v-col cols="12" sm="5">
                                    <v-text-field
                                        v-model="filters.search"
                                        label="Buscar socio o rutina"
                                        prepend-inner-icon="mdi-magnify"
                                        clearable outlined dense hide-details
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="3">
                                    <v-select
                                        v-model="filters.status"
                                        :items="statusFilterItems"
                                        label="Estado"
                                        outlined dense hide-details
                                    ></v-select>
                                </v-col>
                                <v-col cols="12" sm="4" v-if="isAdmin">
                                    <v-select
                                        v-model="filters.coach_id"
                                        :items="coachFilterItems"
                                        label="Coach"
                                        outlined dense hide-details
                                    ></v-select>
                                </v-col>
                            </v-row>
                            <v-data-table
                                :headers="assignmentHeaders"
                                :items="assignments"
                                :loading="loadingAssignments"
                                :server-items-length="assignmentsTotal"
                                :options.sync="assignmentOptions"
                                :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                                class="elevation-0"
                                no-data-text="Sin rutinas asignadas">

                                <template v-slot:item.member="{ item }">
                                    <div class="font-weight-bold">{{ item.member.name }}</div>
                                    <div class="caption grey--text">Teléfono: {{ item.member.phone || 'N/A' }}</div>
                                </template>

                                <template v-slot:item.routine="{ item }">
                                    <div class="font-weight-bold">{{ item.routine_name }}</div>
                                    <div class="caption grey--text">Desde {{ formatDate(item.start_date) }}</div>
                                </template>

                                <template v-slot:item.coach="{ item }">
                                    <div class="font-weight-bold">{{ item.coach ? item.coach.name : 'Sin coach' }}</div>
                                </template>

                                <template v-slot:item.status="{ item }">
                                    <v-chip small :color="statusInfo(item.status).color" text-color="white">
                                        {{ statusInfo(item.status).text }}
                                    </v-chip>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="openEditDialog(item)"
                                                   :disabled="isClosed(item)">
                                                <v-icon small>mdi-pencil</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ isClosed(item) ? 'Asignación cerrada' : 'Editar' }}</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="viewWorkoutPlan(item)">
                                                <v-icon small>mdi-file-document</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver plan y progreso</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="viewDetails(item)">
                                                <v-icon small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver detalles</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>

                    <!-- Tab 2: Socios vinculados -->
                    <v-tab-item>
                        <v-card class="elevation-1 mb-4">
                            <v-card-subtitle class="pa-4 pb-0">
                                <v-icon small class="mr-2">mdi-account-group</v-icon>
                                Socios con rutina activa o en pausa
                            </v-card-subtitle>
                            <v-row class="px-4" dense>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="memberFilter"
                                        label="Buscar por nombre, correo o teléfono"
                                        prepend-inner-icon="mdi-magnify"
                                        clearable outlined dense hide-details
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="4" v-if="isAdmin">
                                    <v-select
                                        v-model="memberCoach"
                                        :items="coachFilterItems"
                                        label="Coach"
                                        outlined dense hide-details
                                    ></v-select>
                                </v-col>
                            </v-row>
                            <v-data-table
                                :headers="linkedHeaders"
                                :items="linkedMembers"
                                :loading="loadingLinked"
                                :server-items-length="linkedTotal"
                                :options.sync="linkedOptions"
                                :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                                class="elevation-0"
                                no-data-text="Aún no tienes socios vinculados">

                                <template v-slot:item.name="{ item }">
                                    <div class="d-flex align-center py-1">
                                        <v-avatar size="36" color="grey lighten-2" class="mr-3">
                                            <v-img v-if="item.avatar" :src="item.avatar"></v-img>
                                            <v-icon v-else small>mdi-account</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="font-weight-bold">{{ item.name }}</div>
                                            <div class="caption grey--text">{{ item.phone || item.email }}</div>
                                        </div>
                                    </div>
                                </template>

                                <template v-slot:item.routines="{ item }">
                                    <div v-for="a in item.assignments" :key="a.id" class="body-2">
                                        {{ a.routine_name }}
                                        <v-chip x-small :color="statusInfo(a.status).color" text-color="white" class="ml-1">
                                            {{ statusInfo(a.status).text }}
                                        </v-chip>
                                        <span class="caption grey--text" v-if="isAdmin && a.coach"> • {{ a.coach.name }}</span>
                                    </div>
                                </template>

                                <template v-slot:item.last_workout_at="{ item }">
                                    <span v-if="item.last_workout_at">{{ formatDate(item.last_workout_at) }}</span>
                                    <span v-else class="caption grey--text">Sin entrenamientos</span>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on"
                                                   @click="viewWorkoutPlan(item.assignments[0])"
                                                   :disabled="!item.assignments.length">
                                                <v-icon small>mdi-file-document</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver plan y progreso</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>

                    <!-- Tab 3: Coaches de Gimnasio (solo administradores) -->
                    <v-tab-item v-if="isAdmin">
                        <v-card class="elevation-1 mb-4">
                            <v-card-title class="pb-0">
                                <div class="subtitle-1">
                                    <v-icon small class="mr-2">mdi-account-tie</v-icon>
                                    Coaches y Calificaciones
                                </div>
                                <v-spacer></v-spacer>
                                <v-btn v-if="isAdmin" color="primary" text @click="openCoachDialog">
                                    <v-icon left>mdi-plus</v-icon>
                                    Nuevo coach
                                </v-btn>
                            </v-card-title>
                            <v-data-table
                                :headers="coachHeaders"
                                :items="coaches"
                                :loading="loadingCoaches"
                                class="elevation-0"
                                no-data-text="Sin coaches registrados">

                                <template v-slot:item.name="{ item }">
                                    <div class="d-flex align-center py-1">
                                        <v-avatar size="40" color="grey lighten-2" class="mr-3">
                                            <v-img v-if="item.avatar" :src="item.avatar"></v-img>
                                            <v-icon v-else>mdi-account</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="font-weight-bold">{{ item.name }}</div>
                                            <div class="caption grey--text">{{ item.email }}</div>
                                        </div>
                                    </div>
                                </template>

                                <template v-slot:item.rating="{ item }">
                                    <div v-if="item.reviews" class="d-flex align-center">
                                        <v-rating :value="item.rating" readonly half-increments dense small
                                                  color="amber" class="mr-2"></v-rating>
                                        <span class="caption">{{ item.rating.toFixed(1) }}</span>
                                    </div>
                                    <span v-else class="caption grey--text">Sin calificaciones</span>
                                </template>

                                <template v-slot:item.reviews="{ item }">
                                    <v-chip small outlined>{{ item.reviews }} reseñas</v-chip>
                                </template>

                                <template v-slot:item.assigned_members="{ item }">
                                    <div class="font-weight-bold">{{ item.assigned_members }}</div>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="openCoachRatings(item)">
                                                <v-icon small>mdi-star</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver calificaciones</span>
                                    </v-tooltip>
                                    <v-tooltip bottom v-if="isAdmin">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="pickAvatar(item)"
                                                   :loading="avatarUploadingId === item.id">
                                                <v-icon small>mdi-camera</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Cambiar foto</span>
                                    </v-tooltip>
                                    <v-tooltip bottom v-if="isAdmin">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="confirmRevoke(item)">
                                                <v-icon small>mdi-account-remove</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Quitar permiso de coach</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>

                    <!-- Tab 4: Historial -->
                    <v-tab-item>
                        <v-card class="elevation-1">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-history</v-icon>
                                Historial de Asignaciones y Cambios
                            </v-card-subtitle>
                            <v-data-table
                                :headers="historyHeaders"
                                :items="history"
                                :loading="loadingHistory"
                                :server-items-length="historyTotal"
                                :options.sync="historyOptions"
                                :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                                class="elevation-0"
                                no-data-text="Sin historial">

                                <template v-slot:item.date="{ item }">
                                    <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                                    <div class="caption grey--text">{{ formatTime(item.date) }}</div>
                                </template>

                                <template v-slot:item.member="{ item }">
                                    <div class="font-weight-bold">{{ item.member.name }}</div>
                                    <div class="caption grey--text">{{ item.routine_name }}</div>
                                </template>

                                <template v-slot:item.action="{ item }">
                                    <v-chip small :color="actionInfo(item.action).color" text-color="white">
                                        {{ actionInfo(item.action).text }}
                                    </v-chip>
                                </template>

                                <template v-slot:item.details="{ item }">
                                    <div class="body-2">{{ item.details }}</div>
                                    <div class="caption grey--text" v-if="item.actor">por {{ item.actor.name }}</div>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>
                </v-tabs-items>
            </v-flex>
        </v-layout>

        <!-- Diálogo: Asignar Rutina -->
        <v-dialog v-model="assignRoutineDialog" max-width="700px" persistent>
            <v-card>
                <v-card-title>
                    <div>
                        <div class="headline">Asignar Rutina de Gimnasio</div>
                        <div class="caption grey--text">Se entrega una copia de la rutina al socio</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="assignRoutineDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="assignForm" v-model="validAssign" lazy-validation>
                        <div class="subtitle-2 font-weight-bold mb-4">Socio</div>
                        <v-autocomplete
                            v-model="newAssignment.member_id"
                            :items="memberItems"
                            :loading="loadingMembers"
                            :search-input.sync="memberSearch"
                            item-text="name"
                            item-value="id"
                            label="Buscar socio por nombre, correo o teléfono"
                            no-filter
                            outlined dense
                            :rules="[v => !!v || 'Se requiere seleccionar un socio']"
                        >
                            <template v-slot:item="{ item }">
                                <div>
                                    <div class="body-2">{{ item.name }}</div>
                                    <div class="caption grey--text">{{ item.email }} {{ item.phone }}</div>
                                </div>
                            </template>
                        </v-autocomplete>

                        <v-divider class="my-4"></v-divider>

                        <div class="subtitle-2 font-weight-bold mb-4">Rutina</div>
                        <v-select
                            v-model="newAssignment.routine_id"
                            :items="libraryRoutines"
                            :loading="loadingRoutines"
                            item-text="name"
                            item-value="id"
                            label="Rutina"
                            outlined dense
                            :rules="[v => !!v || 'Se requiere seleccionar una rutina']"
                        >
                            <template v-slot:item="{ item }">
                                <div>
                                    <div class="body-2">{{ item.name }}</div>
                                    <div class="caption grey--text">
                                        {{ item.exercises.length }} ejercicios • {{ item.created_by ? item.created_by.name : 'Club' }}
                                    </div>
                                </div>
                            </template>
                        </v-select>

                        <template v-if="isAdmin">
                            <div class="subtitle-2 font-weight-bold mb-4">Coach responsable</div>
                            <v-select
                                v-model="newAssignment.coach_id"
                                :items="coaches"
                                item-text="name"
                                item-value="id"
                                label="Coach (opcional)"
                                clearable
                                outlined dense
                            ></v-select>
                        </template>

                        <v-text-field
                            v-model="newAssignment.start_date"
                            type="date"
                            label="Fecha de inicio"
                            outlined dense
                        ></v-text-field>
                        <v-textarea
                            v-model="newAssignment.notes"
                            label="Notas para el socio (opcional)"
                            outlined dense rows="2" counter maxlength="500"
                        ></v-textarea>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="assignRoutineDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveAssignment" :loading="saving">
                        <v-icon left>mdi-content-save</v-icon>
                        Asignar Rutina
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Editar Asignación -->
        <v-dialog v-model="editDialog" max-width="600px" persistent>
            <v-card v-if="currentAssignment">
                <v-card-title>
                    <div>
                        <div class="headline">Editar Asignación</div>
                        <div class="caption grey--text">{{ currentAssignment.member.name }} • {{ currentAssignment.routine_name }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="editDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-select
                        v-model="editForm.status"
                        :items="editableStatuses"
                        item-text="text"
                        item-value="value"
                        label="Estado"
                        outlined dense
                    ></v-select>
                    <v-alert v-if="['completed', 'cancelled'].includes(editForm.status)" type="warning" dense outlined>
                        Al completar o cancelar, la asignación se cierra y ya no se puede reabrir.
                    </v-alert>
                    <v-select
                        v-if="isAdmin"
                        v-model="editForm.coach_id"
                        :items="coaches"
                        item-text="name"
                        item-value="id"
                        label="Coach responsable"
                        clearable
                        outlined dense
                    ></v-select>
                    <v-textarea
                        v-model="editForm.notes"
                        label="Notas para el socio"
                        outlined dense rows="2" counter maxlength="500"
                    ></v-textarea>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="editDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="updateAssignmentData" :loading="saving">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Ver Detalles -->
        <v-dialog v-model="detailsDialog" max-width="700px">
            <v-card v-if="viewingAssignment">
                <v-card-title>
                    <div>
                        <div class="headline">Detalles de Asignación</div>
                        <div class="caption grey--text">{{ viewingAssignment.member.name }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="detailsDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-list dense>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Socio</div>
                                <div class="body-2 font-weight-bold">{{ viewingAssignment.member.name }}</div>
                                <div class="caption">{{ viewingAssignment.member.email }} • {{ viewingAssignment.member.phone }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-2"></v-divider>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Rutina</div>
                                <div class="body-2 font-weight-bold">{{ viewingAssignment.routine_name }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Coach responsable</div>
                                <div class="body-2 font-weight-bold">
                                    {{ viewingAssignment.coach ? viewingAssignment.coach.name : 'Sin coach' }}
                                </div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="viewingAssignment.assigned_by">
                            <v-list-item-content>
                                <div class="caption grey--text">Asignada por</div>
                                <div class="body-2">{{ viewingAssignment.assigned_by.name }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Estado</div>
                                <div>
                                    <v-chip small :color="statusInfo(viewingAssignment.status).color" text-color="white">
                                        {{ statusInfo(viewingAssignment.status).text }}
                                    </v-chip>
                                </div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Inicio</div>
                                <div class="body-2">{{ formatDate(viewingAssignment.start_date) }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="viewingAssignment.end_date">
                            <v-list-item-content>
                                <div class="caption grey--text">Finalización</div>
                                <div class="body-2">{{ formatDate(viewingAssignment.end_date) }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="viewingAssignment.notes">
                            <v-list-item-content>
                                <div class="caption grey--text">Notas</div>
                                <div class="body-2">{{ viewingAssignment.notes }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="viewingAssignment.rating">
                            <v-list-item-content>
                                <div class="caption grey--text">Calificación del socio al coach</div>
                                <v-rating :value="viewingAssignment.rating.stars" readonly dense small color="amber"></v-rating>
                                <div class="caption" v-if="viewingAssignment.rating.comment">
                                    "{{ viewingAssignment.rating.comment }}"
                                </div>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Nuevo coach -->
        <v-dialog v-model="coachDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title>
                    <div>
                        <div class="headline">Nuevo coach</div>
                        <div class="caption grey--text">Se crea un usuario con acceso al panel del gimnasio</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="coachDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>
                <v-card-text>
                    <v-alert v-if="coachErrors" type="error" dense outlined>{{ coachErrors }}</v-alert>
                    <v-form ref="coachForm" v-model="validCoach" lazy-validation>
                        <v-row dense>
                            <v-col cols="12" class="d-flex align-center">
                                <v-avatar size="64" color="grey lighten-2" class="mr-4">
                                    <v-img v-if="coachAvatarPreview" :src="coachAvatarPreview"></v-img>
                                    <v-icon v-else large>mdi-account</v-icon>
                                </v-avatar>
                                <v-file-input
                                    v-model="coachAvatar"
                                    accept="image/jpeg,image/png,image/webp"
                                    label="Foto de perfil (opcional)"
                                    prepend-icon="mdi-camera"
                                    show-size outlined dense hide-details
                                    :rules="[v => !v || v.size <= 5 * 1024 * 1024 || 'Máximo 5 MB']"
                                    @change="onCoachAvatarChange"
                                ></v-file-input>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="coachForm.first_name" label="Nombre" outlined dense
                                              :rules="[v => !!v || 'Requerido']"></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="coachForm.last_name" label="Apellidos" outlined dense
                                              :rules="[v => !!v || 'Requerido']"></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="coachForm.email" label="Correo electrónico" type="email" outlined dense
                                              :rules="[v => /.+@.+\..+/.test(v || '') || 'Correo inválido']"></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="coachForm.cellNumber" label="Celular (+52...)" outlined dense
                                              :rules="[v => !!v || 'Requerido']"></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="coachForm.password" label="Contraseña temporal" outlined dense
                                              :type="showPassword ? 'text' : 'password'"
                                              :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                              @click:append="showPassword = !showPassword"
                                              :rules="[v => (v || '').length >= 6 || 'Mínimo 6 caracteres']"></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="coachForm.birthdate" label="Fecha de nacimiento" type="date"
                                              outlined dense></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="coachDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveCoach" :loading="saving">
                        <v-icon left>mdi-content-save</v-icon>
                        Crear coach
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Calificaciones del coach -->
        <v-dialog v-model="ratingsDialog" max-width="650px" scrollable>
            <v-card v-if="ratingsCoach">
                <v-card-title>
                    <div>
                        <div class="headline">Calificaciones</div>
                        <div class="caption grey--text">{{ ratingsCoach.name }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="ratingsDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>
                <v-card-text>
                    <v-row class="mb-2">
                        <v-col cols="12" sm="5" class="text-center">
                            <div class="display-2 font-weight-bold">
                                {{ ratingsCoach.reviews ? ratingsCoach.rating.toFixed(1) : '—' }}
                            </div>
                            <v-rating :value="ratingsCoach.rating || 0" readonly half-increments dense color="amber"></v-rating>
                            <div class="caption grey--text">{{ ratingsCoach.reviews }} calificaciones</div>
                            <div class="caption grey--text">{{ ratingsCoach.assigned_members }} socios asignados</div>
                        </v-col>
                        <v-col cols="12" sm="7">
                            <div v-for="star in [5, 4, 3, 2, 1]" :key="star" class="d-flex align-center">
                                <span class="caption mr-2" style="width: 12px">{{ star }}</span>
                                <v-icon x-small color="amber" class="mr-2">mdi-star</v-icon>
                                <v-progress-linear :value="distributionPercent(star)" color="amber" height="8" rounded></v-progress-linear>
                                <span class="caption ml-2" style="width: 28px">{{ distribution[star] || 0 }}</span>
                            </div>
                        </v-col>
                    </v-row>
                    <v-divider></v-divider>
                    <v-progress-linear v-if="loadingRatings" indeterminate></v-progress-linear>
                    <div v-if="!loadingRatings && ratings.length === 0" class="text-center grey--text pa-6">
                        Este coach aún no tiene calificaciones
                    </div>
                    <v-list three-line v-if="ratings.length">
                        <v-list-item v-for="r in ratings" :key="r.assignment_id">
                            <v-list-item-content>
                                <v-list-item-title>
                                    {{ r.member ? r.member.name : 'Socio' }}
                                    <v-rating :value="r.stars" readonly dense x-small color="amber" class="d-inline-flex ml-2"></v-rating>
                                </v-list-item-title>
                                <v-list-item-subtitle>{{ r.routine_name }} • {{ formatDate(r.updated_at) }}</v-list-item-subtitle>
                                <div class="body-2" v-if="r.comment">{{ r.comment }}</div>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                    <div class="text-center" v-if="ratingsNext">
                        <v-btn text color="primary" @click="loadMoreRatings" :loading="loadingRatings">Cargar más</v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Diálogo: confirmar quitar coach -->
        <v-dialog v-model="revokeDialog" max-width="450px">
            <v-card v-if="coachToRevoke">
                <v-card-title class="headline">Quitar permiso de coach</v-card-title>
                <v-card-text>
                    {{ coachToRevoke.name }} dejará de ser coach. Su usuario y sus asignaciones se conservan; las
                    asignaciones activas seguirán mostrando su nombre.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="revokeDialog = false">Cancelar</v-btn>
                    <v-btn color="error" text @click="revoke" :loading="saving">Quitar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <input ref="avatarInput" type="file" accept="image/jpeg,image/png,image/webp" class="d-none"
               @change="onAvatarPicked">

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  ASSIGNMENT_ACTIONS, ASSIGNMENT_STATUSES, apiErrorMessage, createAssignment, createCoach, formatDate, formatTime,
  getAssignments, getCoachRatings, getCoaches, getHistory, getLibraryRoutines, getLinkedMembers, getMembers,
  getStaffSummary,
  revokeCoach, statusInfo, updateAssignment, uploadCoachAvatar
} from '@/services/gym'

const today = () => {
  const d = new Date()
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const emptyCoachForm = () => ({
  first_name: '', last_name: '', email: '', cellNumber: '', password: '', birthdate: ''
})

export default {
  name: 'Gym',
  data: () => ({
    activeTab: 0,
    isAdmin: false,
    allowed: false,
    forbidden: false,
    errorMessage: '',
    saving: false,
    summary: { total: 0, active: 0, paused: 0, completed: 0, cancelled: 0, coaches: 0 },

    // Asignaciones
    assignments: [],
    assignmentsTotal: 0,
    loadingAssignments: false,
    assignmentOptions: { page: 1, itemsPerPage: 10 },
    filters: { search: '', status: '', coach_id: '' },
    assignmentHeaders: [
      { text: 'Socio / Teléfono', value: 'member', sortable: false, width: '25%' },
      { text: 'Rutina', value: 'routine', sortable: false, width: '22%' },
      { text: 'Coach', value: 'coach', sortable: false, width: '20%' },
      { text: 'Estado', value: 'status', sortable: false, width: '13%' },
      { text: 'Acciones', value: 'actions', sortable: false, align: 'center', width: '20%' }
    ],

    // Socios vinculados
    linkedMembers: [],
    linkedTotal: 0,
    loadingLinked: false,
    linkedOptions: { page: 1, itemsPerPage: 10 },
    memberFilter: '',
    memberCoach: '',
    linkedTimer: null,
    linkedHeaders: [
      { text: 'Socio', value: 'name', sortable: false, width: '30%' },
      { text: 'Rutinas', value: 'routines', sortable: false, width: '35%' },
      { text: 'Último entrenamiento', value: 'last_workout_at', sortable: false, width: '20%' },
      { text: 'Acciones', value: 'actions', sortable: false, align: 'center', width: '15%' }
    ],

    // Coaches
    coaches: [],
    loadingCoaches: false,
    coachHeaders: [
      { text: 'Nombre / Correo', value: 'name', sortable: false, width: '30%' },
      { text: 'Calificación', value: 'rating', sortable: false, width: '25%' },
      { text: 'Reseñas', value: 'reviews', sortable: false, align: 'center', width: '15%' },
      { text: 'Socios asignados', value: 'assigned_members', sortable: false, align: 'center', width: '15%' },
      { text: 'Acciones', value: 'actions', sortable: false, align: 'center', width: '15%' }
    ],
    coachDialog: false,
    validCoach: false,
    coachForm: emptyCoachForm(),
    coachErrors: '',
    coachAvatar: null,
    coachAvatarPreview: '',
    avatarUploadingId: null,
    avatarTarget: null,
    showPassword: false,
    revokeDialog: false,
    coachToRevoke: null,

    // Calificaciones
    ratingsDialog: false,
    ratingsCoach: null,
    ratings: [],
    ratingsNext: null,
    ratingsPage: 1,
    distribution: {},
    loadingRatings: false,

    // Historial
    history: [],
    historyTotal: 0,
    loadingHistory: false,
    historyOptions: { page: 1, itemsPerPage: 10 },
    historyHeaders: [
      { text: 'Fecha / Hora', value: 'date', sortable: false, width: '18%' },
      { text: 'Socio / Rutina', value: 'member', sortable: false, width: '27%' },
      { text: 'Acción', value: 'action', sortable: false, width: '18%' },
      { text: 'Detalles', value: 'details', sortable: false, width: '37%' }
    ],

    // Diálogos de asignación
    assignRoutineDialog: false,
    validAssign: false,
    newAssignment: { member_id: null, routine_id: null, coach_id: null, start_date: '', notes: '' },
    memberItems: [],
    memberSearch: '',
    loadingMembers: false,
    memberTimer: null,
    libraryRoutines: [],
    loadingRoutines: false,
    editDialog: false,
    currentAssignment: null,
    editForm: { status: '', coach_id: null, notes: '' },
    detailsDialog: false,
    viewingAssignment: null,

    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    searchTimer: null
  }),
  computed: {
    ...mapGetters(['getProfile']),
    statusFilterItems () {
      return [{ text: 'Todos los estados', value: '' }].concat(
        ASSIGNMENT_STATUSES.map(s => ({ text: s.text, value: s.value })))
    },
    coachFilterItems () {
      return [{ text: 'Todos los coaches', value: '' }].concat(
        this.coaches.map(c => ({ text: c.name, value: c.id })))
    },
    editableStatuses () {
      return ASSIGNMENT_STATUSES
    }
  },
  watch: {
    assignmentOptions: {
      handler () { this.loadAssignments() },
      deep: true
    },
    historyOptions: {
      handler () { this.loadHistory() },
      deep: true
    },
    linkedOptions: {
      handler () { this.loadLinkedMembers() },
      deep: true
    },
    memberFilter () { this.reloadLinkedMembers() },
    memberCoach () { this.reloadLinkedMembers() },
    filters: {
      handler () {
        clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
          if (this.assignmentOptions.page !== 1) {
            this.assignmentOptions = Object.assign({}, this.assignmentOptions, { page: 1 })
          } else {
            this.loadAssignments()
          }
        }, 350)
      },
      deep: true
    },
    memberSearch (value) {
      // Al elegir un socio el campo de búsqueda recibe su nombre: no vuelvas a consultar
      const selected = this.memberItems.find(m => m.id === this.newAssignment.member_id)
      if (selected && selected.name === value) return
      clearTimeout(this.memberTimer)
      this.memberTimer = setTimeout(() => this.searchMembers(value), 300)
    }
  },
  async created () {
    await this.loadSummary()
    if (!this.allowed) return
    // La primera pestaña ya montó su tabla (y pidió datos) antes de saber si había permiso: cargar aquí
    this.loadAssignments()
    this.loadCoaches()
  },
  beforeDestroy () {
    clearTimeout(this.searchTimer)
    clearTimeout(this.memberTimer)
    clearTimeout(this.linkedTimer)
  },
  methods: {
    formatDate,
    formatTime,
    statusInfo,
    actionInfo (action) {
      return ASSIGNMENT_ACTIONS[action] || { text: 'Cambio', color: 'grey' }
    },
    isClosed (assignment) {
      return ['completed', 'cancelled'].includes(assignment.status)
    },
    notify (message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },
    fail (error) {
      this.errorMessage = apiErrorMessage(error)
    },
    async loadSummary () {
      try {
        const { data } = await getStaffSummary()
        this.isAdmin = data.is_admin
        this.summary = data.summary
        this.allowed = true
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.forbidden = true
        } else {
          this.fail(error)
        }
      }
    },
    async loadAssignments () {
      if (!this.allowed) return
      this.loadingAssignments = true
      try {
        const { page, itemsPerPage } = this.assignmentOptions
        const params = { page, page_size: itemsPerPage > 0 ? itemsPerPage : 200 }
        if (this.filters.search) params.search = this.filters.search
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.coach_id) params.coach_id = this.filters.coach_id
        const { data } = await getAssignments(params)
        this.assignments = data.results
        this.assignmentsTotal = data.count
        this.summary = Object.assign({}, this.summary, data.summary)
      } catch (error) {
        this.fail(error)
      } finally {
        this.loadingAssignments = false
      }
    },
    reloadLinkedMembers () {
      clearTimeout(this.linkedTimer)
      this.linkedTimer = setTimeout(() => {
        if (this.linkedOptions.page !== 1) {
          this.linkedOptions = Object.assign({}, this.linkedOptions, { page: 1 })
        } else {
          this.loadLinkedMembers()
        }
      }, 350)
    },
    async loadLinkedMembers () {
      if (!this.allowed) return
      this.loadingLinked = true
      try {
        const { page, itemsPerPage } = this.linkedOptions
        const params = { page, page_size: itemsPerPage > 0 ? itemsPerPage : 200 }
        if (this.memberFilter) params.search = this.memberFilter
        if (this.memberCoach) params.coach_id = this.memberCoach
        const { data } = await getLinkedMembers(params)
        this.linkedMembers = data.results
        this.linkedTotal = data.count
      } catch (error) {
        this.fail(error)
      } finally {
        this.loadingLinked = false
      }
    },
    async loadCoaches () {
      if (!this.isAdmin) return
      this.loadingCoaches = true
      try {
        const { data } = await getCoaches()
        this.coaches = data
      } catch (error) {
        this.fail(error)
      } finally {
        this.loadingCoaches = false
      }
    },
    async loadHistory () {
      if (!this.allowed) return
      this.loadingHistory = true
      try {
        const { page, itemsPerPage } = this.historyOptions
        const { data } = await getHistory({ page, page_size: itemsPerPage > 0 ? itemsPerPage : 200 })
        this.history = data.results
        this.historyTotal = data.count
      } catch (error) {
        this.fail(error)
      } finally {
        this.loadingHistory = false
      }
    },
    goToCoachRoutines () {
      this.$router.push('/admin/coach-routines')
    },

    // ---------- asignar
    async openAssignRoutineDialog () {
      this.newAssignment = { member_id: null, routine_id: null, coach_id: null, start_date: today(), notes: '' }
      this.memberItems = []
      this.memberSearch = ''
      this.assignRoutineDialog = true
      this.$nextTick(() => this.$refs.assignForm && this.$refs.assignForm.resetValidation())
      this.searchMembers('')
      this.loadingRoutines = true
      try {
        const { data } = await getLibraryRoutines({ page_size: 200 })
        this.libraryRoutines = data.results
      } catch (error) {
        this.fail(error)
      } finally {
        this.loadingRoutines = false
      }
    },
    async searchMembers (search) {
      this.loadingMembers = true
      try {
        const { data } = await getMembers({ search: search || undefined, page_size: 30 })
        const current = this.memberItems.find(m => m.id === this.newAssignment.member_id)
        this.memberItems = data.results.concat(
          current && !data.results.some(m => m.id === current.id) ? [current] : [])
      } catch (error) {
        this.fail(error)
      } finally {
        this.loadingMembers = false
      }
    },
    async saveAssignment () {
      if (!this.$refs.assignForm.validate()) return
      this.saving = true
      try {
        const body = {
          member_id: this.newAssignment.member_id,
          routine_id: this.newAssignment.routine_id,
          notes: this.newAssignment.notes || null
        }
        if (this.newAssignment.coach_id) body.coach_id = this.newAssignment.coach_id
        if (this.newAssignment.start_date) body.start_date = this.newAssignment.start_date
        await createAssignment(body)
        this.assignRoutineDialog = false
        this.notify('Rutina asignada correctamente')
        this.refreshAll()
      } catch (error) {
        this.notify(apiErrorMessage(error), 'error')
      } finally {
        this.saving = false
      }
    },
    refreshAll () {
      this.loadAssignments()
      this.loadHistory()
      this.loadCoaches()
      this.loadLinkedMembers()
    },

    // ---------- editar / detalle
    openEditDialog (assignment) {
      this.currentAssignment = assignment
      this.editForm = {
        status: assignment.status,
        coach_id: assignment.coach ? this.coachAdminId(assignment.coach.id) : null,
        notes: assignment.notes || ''
      }
      this.editDialog = true
    },
    // La asignación expone el usuario del coach; el API de coaches usa el id del administrador
    coachAdminId (userId) {
      const coach = this.coaches.find(c => c.user_id === userId)
      return coach ? coach.id : null
    },
    async updateAssignmentData () {
      const a = this.currentAssignment
      const body = {}
      if (this.editForm.status !== a.status) body.status = this.editForm.status
      if ((this.editForm.notes || '') !== (a.notes || '')) body.notes = this.editForm.notes || null
      if (this.isAdmin && this.editForm.coach_id !== this.coachAdminId(a.coach ? a.coach.id : null)) {
        body.coach_id = this.editForm.coach_id || null
      }
      if (Object.keys(body).length === 0) {
        this.editDialog = false
        return
      }
      this.saving = true
      try {
        await updateAssignment(a.id, body)
        this.editDialog = false
        this.notify('Asignación actualizada correctamente')
        this.refreshAll()
      } catch (error) {
        this.notify(apiErrorMessage(error), 'error')
      } finally {
        this.saving = false
      }
    },
    viewDetails (assignment) {
      this.viewingAssignment = assignment
      this.detailsDialog = true
    },
    viewWorkoutPlan (assignment) {
      this.$router.push({ path: '/admin/member-workout-plan', query: { assignment_id: assignment.id } })
    },

    // ---------- coaches
    openCoachDialog () {
      this.coachForm = emptyCoachForm()
      this.coachErrors = ''
      this.coachAvatar = null
      this.coachAvatarPreview = ''
      this.showPassword = false
      this.coachDialog = true
      this.$nextTick(() => this.$refs.coachForm && this.$refs.coachForm.resetValidation())
    },
    async saveCoach () {
      if (!this.$refs.coachForm.validate()) return
      this.saving = true
      this.coachErrors = ''
      try {
        const form = new FormData()
        form.set('idUser.first_name', this.coachForm.first_name.trim())
        form.set('idUser.last_name', this.coachForm.last_name.trim())
        form.set('idUser.email', this.coachForm.email.trim().toLowerCase())
        form.set('idUser.cellNumber', this.coachForm.cellNumber.trim())
        form.set('idUser.password', this.coachForm.password)
        form.set('idUser.birthdate', this.coachForm.birthdate || '')
        if (this.coachAvatar) {
          form.append('idUser.avatar', this.coachAvatar)
        } else {
          form.set('idUser.avatar', '')
        }
        form.set('idHousingUnit', this.getProfile.idHousingUnit)
        form.set('fitness_coach', 'true')
        await createCoach(form)
        this.coachDialog = false
        this.notify('Coach creado. Recibirá sus credenciales por correo.')
        this.loadCoaches()
        this.loadSummary()
      } catch (error) {
        const data = error.response && error.response.data
        this.coachErrors = data && typeof data === 'object'
          ? Object.entries(data).map(([k, v]) => `${k.replace('idUser.', '')}: ${JSON.stringify(v).replace(/[[\]{}"]/g, '')}`).join(' · ')
          : apiErrorMessage(error)
      } finally {
        this.saving = false
      }
    },
    onCoachAvatarChange (file) {
      this.coachAvatarPreview = file ? URL.createObjectURL(file) : ''
    },
    pickAvatar (coach) {
      this.avatarTarget = coach
      this.$refs.avatarInput.value = ''
      this.$refs.avatarInput.click()
    },
    async onAvatarPicked (event) {
      const file = event.target.files && event.target.files[0]
      const coach = this.avatarTarget
      if (!file || !coach) return
      if (file.size > 5 * 1024 * 1024) {
        this.notify('La imagen no puede pesar más de 5 MB', 'error')
        return
      }
      this.avatarUploadingId = coach.id
      try {
        const { data } = await uploadCoachAvatar(coach.id, file)
        this.coaches = this.coaches.map(c => c.id === data.id ? data : c)
        this.notify('Foto actualizada')
      } catch (error) {
        this.notify(apiErrorMessage(error), 'error')
      } finally {
        this.avatarUploadingId = null
        this.avatarTarget = null
      }
    },
    confirmRevoke (coach) {
      this.coachToRevoke = coach
      this.revokeDialog = true
    },
    async revoke () {
      this.saving = true
      try {
        await revokeCoach(this.coachToRevoke.id)
        this.revokeDialog = false
        this.notify('Se quitó el permiso de coach', 'info')
        this.loadCoaches()
        this.loadSummary()
      } catch (error) {
        this.notify(apiErrorMessage(error), 'error')
      } finally {
        this.saving = false
      }
    },

    // ---------- calificaciones
    async openCoachRatings (coach) {
      this.ratingsCoach = coach
      this.ratings = []
      this.ratingsNext = null
      this.ratingsPage = 1
      this.distribution = {}
      this.ratingsDialog = true
      await this.fetchRatings()
    },
    async fetchRatings () {
      this.loadingRatings = true
      try {
        const { data } = await getCoachRatings(this.ratingsCoach.id, { page: this.ratingsPage, page_size: 10 })
        this.ratings = this.ratings.concat(data.results)
        this.ratingsNext = data.next
        this.distribution = data.distribution
        this.ratingsCoach = Object.assign({}, this.ratingsCoach, data.coach)
      } catch (error) {
        this.notify(apiErrorMessage(error), 'error')
      } finally {
        this.loadingRatings = false
      }
    },
    loadMoreRatings () {
      this.ratingsPage += 1
      this.fetchRatings()
    },
    distributionPercent (star) {
      const total = this.ratingsCoach ? this.ratingsCoach.reviews : 0
      return total ? ((this.distribution[star] || 0) / total) * 100 : 0
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
